import React from 'react';

interface ProfessionalOrgFallbackProps {
  orgName: string;
  orgId?: string;
  variant?: 'square' | 'wide';
  className?: string;
  sector?: string;
}

const getOrgInitials = (orgName: string): string => {
  if (!orgName) return 'KG';
  const clean = orgName.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  if (!clean) return 'KG';
  
  const ignoredWords = new Set([
    'INC', 'LLC', 'CO', 'CORP', 'LTD', 'INC.', 'LLC.', 
    'CORPORATION', 'COMPANY', 'PARTNERS', 'GROUP', 'SERVICES', 'SERVICE'
  ]);
  const words = clean.split(/\s+/).filter(w => w.length > 0 && !ignoredWords.has(w.toUpperCase()));
  
  if (words.length === 0) return clean.substring(0, 2).toUpperCase();
  if (words.length === 1) {
    return words[0].length >= 2 ? words[0].substring(0, 2).toUpperCase() : words[0].toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

export const ProfessionalOrgFallback: React.FC<ProfessionalOrgFallbackProps> = ({
  orgName,
  orgId,
  className = '',
}) => {
  const name = orgName || 'Company';
  const initials = getOrgInitials(name);

  // Deterministic color palette selection based on orgId or name
  // Restrained professional colors: slate blue, navy, steel blue, charcoal, muted teal
  const getPalette = () => {
    const seedStr = orgId || name;
    let hash = 0;
    for (let i = 0; i < seedStr.length; i++) {
      hash = seedStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % 6;

    const palettes = [
      {
        // Dark blue-gray / steel blue (Kapasi Glass tone)
        bg: 'from-[#0f2d42] via-[#163a52] to-[#1e4860]',
        border: 'border-cyan-500/20',
      },
      {
        // Muted Teal / Deep Slate
        bg: 'from-[#0d3338] via-[#13444a] to-[#1c5860]',
        border: 'border-teal-500/20',
      },
      {
        // Navy / Slate Blue
        bg: 'from-[#0f172a] via-[#1e293b] to-[#2d3d54]',
        border: 'border-sky-500/20',
      },
      {
        // Charcoal & Steel
        bg: 'from-[#18222d] via-[#22303e] to-[#2c3e50]',
        border: 'border-slate-400/20',
      },
      {
        // Deep Indigo / Steel Navy
        bg: 'from-[#131b2e] via-[#1c2942] to-[#263756]',
        border: 'border-blue-500/20',
      },
      {
        // Dark Cyan Teal
        bg: 'from-[#0a2e36] via-[#123e48] to-[#1a4f5b]',
        border: 'border-emerald-500/20',
      },
    ];

    return palettes[index];
  };

  const palette = getPalette();

  return (
    <div 
      id={`org-fallback-${orgId || 'gen'}`}
      className={`w-full h-full aspect-square rounded-2xl bg-gradient-to-br ${palette.bg} ${palette.border} border flex items-center justify-center relative overflow-hidden select-none shadow-xs ${className}`}
    >
      {/* Translucent diagonal glass pane sheen overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.08] to-white/[0.22] pointer-events-none" 
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 0)' }}
      />
      {/* Secondary subtle inner highlight */}
      <div className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none" />

      {/* Large, Centered, Semibold White Monogram Initials */}
      <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-wider text-white drop-shadow-xs z-10 uppercase font-sans">
        {initials}
      </span>
    </div>
  );
};

